import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../../../web/components-v2/Dialog';
import { Input } from '../../../../../../web/components-v2/Input';
import { TextArea } from '../../../../../../web/components-v2/TextArea';
import { useSavedMessages } from '../../hooks/useSavedMessages';
import { SavedMessage } from '../../types/savedMessages';
import { MessageListItem } from './MessageListItem';
import { s } from './styles';

interface ManageMessagesDialogProps {
	isOpen: boolean;
	onClose: () => void;
}

export const ManageMessagesDialog: React.FC<ManageMessagesDialogProps> = ({ isOpen, onClose }) => {
	const { messages, addMessage, updateMessage, deleteMessage, reorderMessages } = useSavedMessages();

	const [editingId, setEditingId] = React.useState<string | null>(null);
	const [isCreating, setIsCreating] = React.useState(false);
	const [formData, setFormData] = React.useState({ label: '', content: '' });
	const [errors, setErrors] = React.useState({ label: '', content: '' });

	// Validation logic
	const validate = () => {
		const newErrors = { label: '', content: '' };

		if (!formData.label.trim()) {
			newErrors.label = 'Label is required';
		} else if (formData.label.length > 50) {
			newErrors.label = 'Label must be 50 characters or less';
		} else {
			// Check for duplicate labels
			const isDuplicate = messages.some(
				msg => msg.label.toLowerCase() === formData.label.toLowerCase() && msg.id !== editingId
			);
			if (isDuplicate) {
				newErrors.label = 'A message with this label already exists';
			}
		}

		if (!formData.content.trim()) {
			newErrors.content = 'Content is required';
		} else if (formData.content.length > 2000) {
			newErrors.content = 'Content must be 2000 characters or less';
		}

		setErrors(newErrors);
		return !newErrors.label && !newErrors.content;
	};

	const handleSave = () => {
		if (!validate()) return;

		if (isCreating) {
			addMessage(formData.label, formData.content);
		} else if (editingId) {
			updateMessage(editingId, formData.label, formData.content);
		}

		resetForm();
	};

	const resetForm = () => {
		setFormData({ label: '', content: '' });
		setEditingId(null);
		setIsCreating(false);
		setErrors({ label: '', content: '' });
	};

	const handleEdit = (message: SavedMessage) => {
		setEditingId(message.id);
		setFormData({ label: message.label, content: message.content });
	};

	const handleDelete = (id: string) => {
		// Simple confirmation
		if (window.confirm('Are you sure you want to delete this message?')) {
			deleteMessage(id);
		}
	};

	const handleReorder = (reorderedMessages: SavedMessage[]) => {
		reorderMessages(reorderedMessages);
	};

	// Reset form when dialog closes
	React.useEffect(() => {
		if (!isOpen) {
			resetForm();
		}
	}, [isOpen]);

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Manage Custom Messages</DialogTitle>
				</DialogHeader>
				<DialogBody styles={[s.dialogBody]}>
					{!isCreating && !editingId ? (
						<>
							<div className={css(s.messageList)}>
								{messages.length === 0 ? (
									<div className={css(s.emptyState)}>No saved messages yet. Create your first custom message.</div>
								) : (
									messages.map((message, index) => (
										<MessageListItem
											key={message.id}
											message={message}
											index={index}
											onEdit={() => handleEdit(message)}
											onDelete={() => handleDelete(message.id)}
											onReorder={handleReorder}
											allMessages={messages}
										/>
									))
								)}
							</div>
							<Button onClick={() => setIsCreating(true)} variant='secondary' styles={[s.addButton]}>
								Add New Message
							</Button>
						</>
					) : (
						<div className={css(s.form)}>
							<div className={css(s.formField)}>
								<label className={css(s.label)}>
									Label <span className={css(s.required)}>*</span>
								</label>
								<Input
									value={formData.label}
									onChange={e => setFormData({ ...formData, label: e.target.value })}
									placeholder='e.g., Sales Coaching'
									maxLength={50}
								/>
								<div className={css(s.helperText)}>{formData.label.length}/50 characters</div>
								{errors.label && <div className={css(s.errorText)}>{errors.label}</div>}
							</div>

							<div className={css(s.formField)}>
								<label className={css(s.label)}>
									Message Content <span className={css(s.required)}>*</span>
								</label>
								<TextArea
									value={formData.content}
									onChange={e => setFormData({ ...formData, content: e.target.value })}
									placeholder='Enter custom instructions for the AI analysis...'
									rows={8}
									maxLength={2000}
								/>
								<div className={css(s.helperText)}>{formData.content.length}/2000 characters</div>
								{errors.content && <div className={css(s.errorText)}>{errors.content}</div>}
							</div>
						</div>
					)}
				</DialogBody>
				<DialogFooter>
					{isCreating || editingId ? (
						<>
							<Button variant='secondary' onClick={resetForm}>
								Cancel
							</Button>
							<Button onClick={handleSave}>{isCreating ? 'Create' : 'Save'}</Button>
						</>
					) : (
						<Button onClick={onClose}>Done</Button>
					)}
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
